import type { ActionDecision } from "../api/action-capabilities";
import type { BootstrapResponse, ProofEligibilityResponse } from "../api/schemas";
import type { MiningSnapshot } from "../mining-engine";
import { sameAddress } from "./model-helpers";
import { responseNow } from "../api/response-time";
import { validConfirmedTierDisplay } from "./view-mappers";

export const SCANNER_REASONS = ["TOKEN_TRADE_SCAN_IN_PROGRESS", "SCANNER_NOT_CAUGHT_UP"] as const;
export function scannerOnly(reasons: readonly string[]): boolean {
  return reasons.length > 0 && reasons.every((reason) => SCANNER_REASONS.some((code) => code === reason));
}

export interface ProofDecisionContext {
  proofId: string;
  challengeId: string;
  matches: boolean;
  permission: ActionDecision;
}

/** Ignore scan/display updates; fence lifecycle replacements even within one React batch. */
export function proofLifecycleKey(mining: Pick<MiningSnapshot, "proof" | "challenge">): string {
  return JSON.stringify([mining.proof?.proofId, mining.proof?.proofStatus, mining.proof?.challengeId,
    mining.challenge?.challengeId, mining.challenge?.walletAddress, mining.challenge?.tokenAddress,
    mining.challenge?.chainId, mining.challenge?.instanceId, mining.challenge?.challengeCommitment]);
}

export function proofContextMatches(bootstrap: BootstrapResponse | null, mining: Pick<MiningSnapshot, "proof" | "challenge">,
  authenticated: boolean, account: string | null, token: BootstrapResponse["tokens"][number] | null): boolean {
  const { proof, challenge } = mining;
  return Boolean(bootstrap && proof && challenge && authenticated && account && token && proof.proofStatus === "VERIFIED" &&
    sameAddress(proof.challengeId, challenge.challengeId) && sameAddress(challenge.walletAddress, account) &&
    sameAddress(challenge.tokenAddress, token.tokenAddress) && String(challenge.chainId) === String(bootstrap.sourceChainId) &&
    token.chainId === bootstrap.sourceChainId && bootstrap.deployment.sourceChainId === bootstrap.sourceChainId &&
    bootstrap.deployment.sourceTokenStatus === "REGISTERED");
}
export interface ProofPreparationDecision extends ActionDecision {
  usableTiers: number[] | null;
  source: "CURRENT" | "LAST_CONFIRMED_SNAPSHOT" | null;
  message: string;
}

/** Only confirmed lifecycle data can retire a proof; availability is unrelated. */
export function proofRetirementStatus(proof: ProofEligibilityResponse | null,
  context: Pick<ProofDecisionContext, "proofId" | "challengeId" | "matches">,
  now = proof ? responseNow(proof) : null): "EXPIRED" | "CONSUMED" | null {
  if (!proof || !context.matches || proof.proofId.toLowerCase() !== context.proofId.toLowerCase() ||
    proof.challengeId.toLowerCase() !== context.challengeId.toLowerCase()) return null;
  if (proof.proofStatus === "EXPIRED" || proof.proofStatus === "CONSUMED") return proof.proofStatus;
  return proof.proofStatus === "VERIFIED" && now !== null && Number.isFinite(now) &&
    proof.expiresAt <= Math.floor(now / 1_000) ? "EXPIRED" : null;
}

function lifecycleReason(proof: ProofEligibilityResponse, context: Pick<ProofDecisionContext, "proofId" | "challengeId" | "matches">,
  now: number | null): string | null {
  if (!context.matches || proof.proofId.toLowerCase() !== context.proofId.toLowerCase() ||
    proof.challengeId.toLowerCase() !== context.challengeId.toLowerCase()) return "PROOF_CONTEXT_CHANGED";
  if (proof.proofStatus !== "VERIFIED") return proof.proofStatus === "EXPIRED" ? "PROOF_EXPIRED" : "PROOF_CONSUMED";
  if (now === null || !Number.isFinite(now)) return "PROOF_SERVER_TIME_UNAVAILABLE";
  if (proof.expiresAt <= Math.floor(now / 1_000)) return "PROOF_EXPIRED";
  return null;
}

function blocked(reasons: string[], message: string, usableTiers: number[] | null = null,
  source: ProofPreparationDecision["source"] = null, state: ActionDecision["state"] = "BLOCKED"): ProofPreparationDecision {
  return { state, reasons, message, usableTiers, source };
}

function proofTierOptions(proof: ProofEligibilityResponse): Pick<ProofPreparationDecision, "usableTiers" | "source"> {
  if (proof.eligibilityAvailable && proof.blockingReasons.length === 0) {
    return { usableTiers: proof.eligibleTiers, source: "CURRENT" };
  }
  if (!proof.eligibilityAvailable && scannerOnly(proof.blockingReasons) && validConfirmedTierDisplay(proof.tierDisplay)) {
    return { usableTiers: proof.tierDisplay!.eligibleTiers, source: "LAST_CONFIRMED_SNAPSHOT" };
  }
  return { usableTiers: null, source: null };
}

/** Browsing only: mint policy and preparation hints never authorize actions through this path. */
export function proofTierDisplay(proof: ProofEligibilityResponse | null,
  context: Pick<ProofDecisionContext, "proofId" | "challengeId" | "matches">,
  now = proof ? responseNow(proof) : null): Pick<ProofPreparationDecision, "usableTiers" | "source"> {
  return !proof || lifecycleReason(proof, context, now) ? { usableTiers: null, source: null } : proofTierOptions(proof);
}

/** Permission to request backend verification only. No transaction authority. */
export function proofPreparationDecision(proof: ProofEligibilityResponse | null, context: ProofDecisionContext,
  selectedTier?: number, now = proof ? responseNow(proof) : null): ProofPreparationDecision {
  const reasons = proof?.blockingReasons ?? [];
  if (!proof) return blocked(["PROOF_ELIGIBILITY_UNAVAILABLE"], "Refresh proof eligibility and try again.", null, null, "UNKNOWN");
  const lifecycle = lifecycleReason(proof, context, now);
  if (lifecycle) return blocked([lifecycle, ...reasons], lifecycle === "PROOF_EXPIRED" ?
    "The proof has expired. Mine a new proof." : lifecycle === "PROOF_CONSUMED" ?
    "The proof has been consumed. Mine a new proof." : "Cannot verify the current proof. Refresh and try again.");
  if (context.permission.state !== "ALLOWED") return blocked([...context.permission.reasons, ...reasons],
    "Mint preparation is unavailable under the current policy.", null, null, context.permission.state);
  const options = proofTierOptions(proof);
  let tiers = options.usableTiers;
  let source = options.source;
  if (proof.preparationCheckAvailable === false) {
    const belowThreshold = tiers?.length === 0;
    return blocked(["PREPARATION_CHECK_UNAVAILABLE", ...reasons], belowThreshold ?
      "Current net buy does not meet any tier threshold." : "The backend has disabled preparation for this proof. Refresh and try again.",
      belowThreshold ? tiers : null, belowThreshold ? source : null);
  }
  // A validated snapshot can describe below-threshold data without granting fallback permission.
  if (!proof.eligibilityAvailable && proof.preparationCheckAvailable !== true) { tiers = null; source = null; }
  if (tiers === null) return blocked(reasons.length ? [...reasons] : ["PROOF_ELIGIBILITY_UNAVAILABLE"],
    "Mint eligibility is temporarily unavailable. Refresh and try again.");
  if (tiers.length === 0 || selectedTier !== undefined && !tiers.includes(selectedTier)) return blocked([...reasons, "SELECTED_TIER_UNAVAILABLE"],
    "Current net buy does not meet the selected tier's requirements. Choose another tier and prepare again.", tiers, source);
  return { state: "ALLOWED", reasons: [...reasons], usableTiers: tiers, source,
    message: "The backend will recheck eligibility before minting." };
}

/** Call only under an already validated signed review; preflight still owns send checks. */
export function assertProofForPreparedMint(proof: ProofEligibilityResponse,
  context: Pick<ProofDecisionContext, "proofId" | "challengeId" | "matches">,
  tier: number, now = responseNow(proof)): void {
  const lifecycle = lifecycleReason(proof, context, now);
  if (lifecycle) throw new Error(`${lifecycle}: ${lifecycle === "PROOF_EXPIRED" ? "The proof has expired." : "The proof changed or its expiry cannot be verified."} Prepare and review again.`);
  if (proof.eligibilityAvailable ? proof.blockingReasons.length !== 0 || !proof.eligibleTiers?.some((value) => value === tier) :
    !scannerOnly(proof.blockingReasons)) {
    throw new Error(`Proof or tier eligibility changed. Prepare and review again. ${proof.blockingReasons.join(", ")}`);
  }
}
